
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { kycService, KycStatusResponse } from '../../services/kyc.service';
import { userTradeService, UserTrade } from '../../services/user.trade.service';
import { userWithdrawalService } from '../../services/user.withdrawal.service';
import { userDetailService } from '../../services/user.detail.service';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { Copy, Check, TrendingUp, ShieldAlert, Award, Activity, Wallet, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Data State
  const [kycStatus, setKycStatus] = useState<KycStatusResponse | null>(null);
  const [btcAddress, setBtcAddress] = useState<string>("Loading...");
  const [trades, setTrades] = useState<UserTrade[]>([]);
  const [approvedWithdrawals, setApprovedWithdrawals] = useState<number>(0);

  // Mock Price Data (Stay untouched as requested)
  const priceData = [
    { time: '00:00', price: 62400 },
    { time: '04:00', price: 61800 },
    { time: '08:00', price: 63200 },
    { time: '12:00', price: 62900 },
    { time: '16:00', price: 64500 },
    { time: '20:00', price: 63800 },
    { time: '23:59', price: 65100 },
  ];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [kycRes, btcRes, tradesRes, withdrawalsRes] = await Promise.all([
        kycService.getKycStatus(),
        userDetailService.getBtcAddress(),
        userTradeService.listTrades({}), // Fetch all for stats
        userWithdrawalService.listWithdrawals({ limit: 1000 }) // Fetch for allowance
      ]);

      if (kycRes.success) setKycStatus(kycRes);
      if (btcRes.success) setBtcAddress(btcRes.data.address);
      if (tradesRes.success) setTrades(tradesRes.data);
      if (withdrawalsRes.success) {
        const approved = withdrawalsRes.data
          .filter(w => w.status === 'APPROVED')
          .reduce((sum, w) => sum + parseFloat(w.amount || '0'), 0);
        setApprovedWithdrawals(approved);
      }
    } catch (error) {
      console.error('Core Sync Failure:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = useMemo(() => {
    const wins = trades.filter(t => t.outcome === 'PROFIT' || t.outcome === 'DEPOSIT');
    const losses = trades.filter(t => t.outcome === 'LOSS');

    const totalProfit = wins.reduce((sum, t) => sum + parseFloat(t.payout || '0'), 0);
    const totalLoss = losses.reduce((sum, t) => sum + parseFloat(t.principal || '0'), 0);

    // Formula: Total Profit Trade - (Total Loss Trade + Total Approved Withdrawals)
    const allowance = Math.max(0, totalProfit - (totalLoss + approvedWithdrawals));

    return {
      total: trades.length,
      wins: wins.length,
      losses: losses.length,
      winRate: trades.length > 0 ? ((wins.length / trades.length) * 100).toFixed(1) : '0.0',
      allowance
    };
  }, [trades, approvedWithdrawals]);

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(btcAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-brand-primary" size={48} />
        <p className="text-brand-muted text-[10px] font-black uppercase tracking-[0.3em] animate-pulse">Syncing Nexus Protocols...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-6 duration-1000">

      {/* 1. Ultra Modern Welcome */}
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-8 pt-4">
        <div className="space-y-2">
          <h2 className="text-brand-muted text-[11px] font-black uppercase tracking-[0.5em] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-primary animate-pulse shadow-[0_0_10px_#FFCE1B]"></span>
            Nexus Core Protocol
          </h2>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter">
            Control, <span className="text-brand-primary">{user?.name.split(' ')[0] || 'Trader'}</span>
          </h1>
        </div>

        <div className="flex items-center gap-6 glass-card px-8 py-5 rounded-[2rem]">
          <div className="text-right">
            <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest mb-1">Global Market Node</p>
            <p className="text-2xl font-black text-white font-mono">$65,102.48</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary border border-brand-primary/20">
            <TrendingUp size={28} />
          </div>
        </div>
      </section>

      {/* 2. KYC Compliance Alert */}
      {kycStatus?.status !== 'APPROVED' && (
        <section className="bg-brand-primary/5 border border-brand-primary/20 rounded-[2.5rem] p-8 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-primary opacity-10 blur-[60px] translate-x-10 -translate-y-10"></div>
          <div className="flex items-center gap-6 z-10">
            <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center border ${kycStatus?.status === 'PENDING' ? 'bg-brand-primary/10 text-brand-primary border-brand-primary/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
              <ShieldAlert size={32} />
            </div>
            <div className="space-y-1">
              <h3 className="text-white text-lg font-black uppercase tracking-tight">
                {kycStatus?.status === 'PENDING' ? 'Submitted Pending State' : 'System Compliance Pending'}
              </h3>
              <p className="text-brand-muted text-sm max-w-md leading-relaxed">
                {kycStatus?.message || 'Access to capital flows requires identity synchronization. Please finalize your compliance documents.'}
              </p>
            </div>
          </div>
          {kycStatus?.status !== 'PENDING' && (
            <Link to="/user/profile" className="px-10 py-4 bg-brand-primary text-black text-[11px] font-black rounded-3xl hover:shadow-[0_0_20px_rgba(255,206,27,0.4)] transition-all uppercase tracking-widest z-10 shrink-0 text-center">
              Update Documents
            </Link>
          )}
        </section>
      )}

      {/* 3. High Performance Grid */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <MetricCard label="Total Executions" value={stats.total} icon={<Activity size={24} />} />
        <MetricCard label="Winning Trades" value={stats.wins} color="text-emerald-400" icon={<Award size={24} />} glowColor="bg-emerald-500/10" />
        <MetricCard label="Loss Mitigation" value={stats.losses} color="text-red-400" icon={<ShieldAlert size={24} />} />
        <MetricCard label="Win Probability" value={`${stats.winRate}%`} color="text-brand-primary" icon={<TrendingUp size={24} />} sub="Optimized" />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* 4. Interactive Market Feed (MARKET VELOCITY - UNTOUCHED) */}
        <section className="lg:col-span-8 glass-card rounded-[3rem] p-10 relative overflow-hidden group">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h3 className="text-2xl font-black text-white tracking-tight">Market Velocity</h3>
              <p className="text-brand-muted text-[10px] uppercase tracking-[0.3em] font-black">BTC/USD Live Feed</p>
            </div>
            <div className="flex bg-brand-dark/50 p-1.5 rounded-2xl border border-white/5">
              {['1H', '1D', '1W', '1M'].map(t => (
                <button key={t} className={`px-5 py-2 text-[10px] font-black rounded-xl transition-all duration-500 ${t === '1D' ? 'bg-brand-primary text-black shadow-lg shadow-brand-primary/20' : 'text-brand-muted hover:text-white'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={priceData}>
                <defs>
                  <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFCE1B" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#FFCE1B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="10 10" vertical={false} stroke="rgba(255,255,255,0.03)" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: '#71717A', fontSize: 10, fontWeight: 700 }} />
                <YAxis domain={['dataMin - 100', 'dataMax + 100']} hide />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0F0F0F', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', padding: '16px' }}
                  itemStyle={{ color: '#FFCE1B', fontSize: '14px', fontWeight: 900 }}
                />
                <Area type="monotone" dataKey="price" stroke="#FFCE1B" strokeWidth={4} fill="url(#chartGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* 5. Liquid Assets Card */}
        <section className="lg:col-span-4 glass-card rounded-[3rem] p-10 flex flex-col justify-between group border-brand-primary/10">
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Liquid Equity</h3>
              <Wallet className="text-brand-primary" size={24} />
            </div>

            <div className="space-y-2">
              <p className="text-5xl font-black text-white tracking-tighter font-mono">${stats.allowance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              <div className="flex items-center gap-2 text-emerald-400 font-black text-[11px] uppercase tracking-widest">
                <TrendingUp size={14} />
                +Available for payout
              </div>
            </div>

            <div className="pt-8 space-y-4">
              <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-widest text-brand-muted">
                <span>Asset Allocator</span>
                <span className="text-white">BTC Protocol</span>
              </div>
              <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden border border-white/5">
                <div className="h-full bg-brand-primary rounded-full animate-pulse shadow-[0_0_15px_#FFCE1B]" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>

          <Link to="/user/trades" className="w-full group/btn mt-12 py-5 bg-white/5 border border-white/10 rounded-[1.5rem] flex items-center justify-between px-6 hover:bg-white/10 transition-all active:scale-[0.98]">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Execution Ledger</span>
            <ChevronRight size={18} className="text-brand-primary group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </section>
      </div>

      {/* 6. Protocol Address Station */}
      <section className="glass-card rounded-[3.5rem] p-12 border-dashed border-white/10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-primary/40 to-transparent"></div>
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 relative z-10">
          <div className="space-y-4 text-center lg:text-left">
            <h3 className="text-[11px] font-black text-brand-primary uppercase tracking-[0.4em]">Primary Deposit Protocol</h3>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <code className="bg-black/80 border border-white/10 px-8 py-5 rounded-3xl text-white font-mono text-xl md:text-2xl break-all shadow-inner tracking-tight">
                {btcAddress}
              </code>
              <button
                onClick={handleCopyAddress}
                className={`p-5 rounded-3xl transition-all duration-500 w-full sm:w-auto flex items-center justify-center gap-3 min-w-[180px] ${copied ? 'bg-emerald-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)]' : 'bg-brand-primary text-black font-black uppercase text-[11px] tracking-widest hover:scale-105'
                  }`}
              >
                {copied ? <Check size={24} /> : <><Copy size={20} /> Copy Hash</>}
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center lg:items-end gap-2 shrink-0">
            <div className="flex gap-2">
              {[1, 2, 3].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-brand-primary animate-pulse" style={{ animationDelay: `${i * 0.2}s` }}></div>)}
            </div>
            <p className="text-[10px] text-brand-muted font-black uppercase tracking-[0.2em]">Validated by NexusShield™</p>
          </div>
        </div>
      </section>
    </div>
  );
};

const MetricCard: React.FC<{ label: string, value: string | number, icon: React.ReactNode, color?: string, glowColor?: string, sub?: string }> = ({ label, value, icon, color = 'text-white', glowColor = 'bg-brand-primary/5', sub }) => (
  <div className={`glass-card p-10 rounded-[3rem] border border-white/5 hover:border-brand-primary/30 transition-all duration-700 group relative overflow-hidden`}>
    <div className={`absolute top-0 right-0 w-24 h-24 blur-[50px] opacity-0 group-hover:opacity-100 transition-opacity ${glowColor}`}></div>
    <div className="flex justify-between items-start mb-8">
      <div className="p-4 bg-brand-dark/80 rounded-2xl border border-white/10 text-brand-muted group-hover:text-brand-primary transition-colors duration-500">
        {icon}
      </div>
      {sub && <span className="text-[9px] font-black text-brand-primary border border-brand-primary/20 px-2.5 py-1 rounded-full uppercase tracking-widest">{sub}</span>}
    </div>
    <div className="space-y-1">
      <p className="text-brand-muted text-[10px] font-black uppercase tracking-[0.25em]">{label}</p>
      <p className={`text-4xl font-black tracking-tighter font-mono ${color}`}>{value}</p>
    </div>
  </div>
);

export default Dashboard;
